import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { submitOnboarding } from "@/lib/api";
import { AnalyzingOverlay } from "@/components/AnalyzingOverlay";

const JOB_TYPES = ["Designer", "Sviluppatore", "Consulente", "Creator", "Personal trainer", "Altro"];
const QUICK_DAYS = [1, 5, 10, 15, 20, 25];

const fade = { duration: 0.25, ease: [0.16, 1, 0.3, 1] };

export default function Onboarding({ onComplete }) {
  const [step, setStep] = useState(0);
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [jobType, setJobType] = useState(null);
  const [payday, setPayday] = useState(15);
  const [budget, setBudget] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const connect = () => {
    setConnecting(true);
    setTimeout(() => {
      setConnecting(false);
      setConnected(true);
      setTimeout(() => setStep(1), 700);
    }, 1400);
  };

  const pickJob = (j) => {
    setJobType(j);
    setTimeout(() => setStep(2), 250);
  };

  const submit = async () => {
    const b = parseFloat(String(budget).replace(",", "."));
    if (!b || b <= 0) {
      toast.error("Inserisci un budget mensile valido");
      return;
    }
    setSubmitting(true);
    try {
      const res = await submitOnboarding({
        job_type: jobType || "Altro",
        payment_day: payday,
        monthly_budget: b,
      });
      onComplete(res.profile);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Qualcosa è andato storto. Riprova.");
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-dvh flex-col px-5 pb-8" data-testid="onboarding-screen">
      {submitting && (
        <AnalyzingOverlay
          messages={[
            "Leggo le transazioni del conto demo…",
            "Classifico ogni movimento…",
            "Confronto con il tuo storico…",
            "Calcolo le proiezioni…",
            "Genero i tuoi insight…",
          ]}
        />
      )}

      {/* top bar */}
      <div className="flex items-center justify-between pt-6">
        <div className="h-9 w-9">
          {step > 0 && !submitting && (
            <button
              onClick={() => setStep(step - 1)}
              data-testid="onboarding-back-button"
              aria-label="Indietro"
              className="flex h-9 w-9 items-center justify-center rounded-[6px] border border-white/[0.08] text-white/60 hover:bg-white/[0.05]"
            >
              <ArrowLeft size={16} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {[0, 1, 2, 3].map((i) => (
            <span
              key={i}
              className={`h-[2px] transition-colors ${i <= step ? "w-6 bg-[#00FF87]" : "w-4 bg-white/12"}`}
            />
          ))}
          <span className="mono ml-2 text-[10px] tracking-[0.15em] text-white/35">
            0{step + 1}/04
          </span>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {step === 0 && (
          <motion.div
            key="s0"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={fade}
            className="flex flex-1 flex-col"
          >
            <div className="flex-1 pt-14">
              <h1 className="mono flex items-center text-[52px] font-light leading-none tracking-[-2px] text-white">
                LIQO<span className="liqo-blink ml-1 text-[#00FF87]">▌</span>
              </h1>
              <p className="mt-4 text-base font-light text-white/75">Il tuo CFO AI. Ti parla prima lui.</p>
              <div className="mt-10 space-y-4">
                {[
                  ["#00FF87", "Legge e classifica le transazioni da solo"],
                  ["#4D9CFF", "Ti avvisa solo quando c'è qualcosa di rilevante"],
                  ["#FF4D4D", "Zero budget manuali, zero grafici da interpretare"],
                ].map(([c, t]) => (
                  <div key={t} className="flex items-start gap-3">
                    <span className="mono mt-0.5 text-xs" style={{ color: c }}>▸</span>
                    <p className="text-sm font-light leading-6 text-white/60">{t}</p>
                  </div>
                ))}
              </div>
            </div>

            {connected ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.15 }}
                className="flex items-center gap-3 rounded-[12px] border border-white/[0.06] bg-[#0D0D14] p-4 shadow-[0_0_0_1px_rgba(0,255,135,0.18)]"
                data-testid="onboarding-bank-connected"
              >
                <span className="mono text-lg text-[#00FF87]">⊕</span>
                <div className="flex-1">
                  <p className="text-sm font-light text-white/90">Revolut · Conto demo</p>
                  <p className="caps-tag mt-0.5 text-white/35">Collegato — 91 giorni di storico</p>
                </div>
                <Check size={16} className="text-[#00FF87]" />
              </motion.div>
            ) : (
              <button
                onClick={connect}
                disabled={connecting}
                data-testid="onboarding-connect-bank-button"
                className="flex h-12 w-full items-center justify-center gap-3 rounded-[6px] border border-[#00FF87]/40 bg-transparent text-[11px] font-medium uppercase tracking-[0.25em] text-[#00FF87] shadow-[0_0_24px_rgba(0,255,135,0.1)] hover:bg-[#00FF87]/[0.06] active:scale-[0.98] disabled:opacity-60"
              >
                {connecting ? (
                  <>
                    <span className="flex items-center gap-1">
                      <span className="liqo-thinking-dot" />
                      <span className="liqo-thinking-dot" />
                      <span className="liqo-thinking-dot" />
                    </span>
                    Connessione a Revolut…
                  </>
                ) : (
                  "Collega il tuo conto"
                )}
              </button>
            )}
            <p className="caps-tag mt-3 text-center !tracking-[0.15em] text-white/25">
              Conto demo per iniziare · collega la banca reale dopo l'accesso
            </p>
          </motion.div>
        )}

        {step === 1 && (
          <motion.div
            key="s1"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={fade}
            className="flex-1 pt-12"
          >
            <p className="caps-tag text-[#00FF87]/70">01 / 03</p>
            <h2 className="mt-3 text-2xl font-light tracking-[-0.02em] text-white/95">Che lavoro fai?</h2>
            <p className="mt-1.5 text-sm font-light text-white/45">Aiuta l'agente a capire le tue entrate.</p>
            <div className="mt-8 flex flex-wrap gap-2" data-testid="onboarding-job-type">
              {JOB_TYPES.map((j) => (
                <button
                  key={j}
                  onClick={() => pickJob(j)}
                  data-testid={`onboarding-job-${j.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`h-11 rounded-[6px] border px-5 text-[11px] font-medium uppercase tracking-[0.18em] active:scale-[0.98] ${
                    jobType === j
                      ? "border-[#00FF87]/50 bg-[#00FF87]/[0.06] text-[#00FF87] shadow-[0_0_16px_rgba(0,255,135,0.12)]"
                      : "border-white/[0.12] bg-transparent text-white/70 hover:bg-white/[0.05]"
                  }`}
                >
                  {j}
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div
            key="s2"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={fade}
            className="flex flex-1 flex-col pt-12"
          >
            <p className="caps-tag text-[#00FF87]/70">02 / 03</p>
            <h2 className="mt-3 text-2xl font-light tracking-[-0.02em] text-white/95">Quando ti pagano di solito?</h2>
            <p className="mt-1.5 text-sm font-light text-white/45">Giorno del mese in cui aspetti i pagamenti.</p>
            <div className="mt-8 flex flex-wrap gap-2">
              {QUICK_DAYS.map((d) => (
                <button
                  key={d}
                  onClick={() => setPayday(d)}
                  data-testid={`onboarding-payday-${d}`}
                  className={`mono h-11 w-14 rounded-[6px] border text-sm font-light tabular active:scale-[0.98] ${
                    payday === d
                      ? "border-[#00FF87]/50 bg-[#00FF87]/[0.06] text-[#00FF87] shadow-[0_0_16px_rgba(0,255,135,0.12)]"
                      : "border-white/[0.12] bg-transparent text-white/70 hover:bg-white/[0.05]"
                  }`}
                >
                  {String(d).padStart(2, "0")}
                </button>
              ))}
            </div>
            <div className="mt-5 flex items-center gap-3">
              <label className="caps-tag text-white/40">Altro giorno</label>
              <input
                type="number"
                min={1}
                max={31}
                value={payday}
                onChange={(e) => {
                  const v = parseInt(e.target.value || "0", 10);
                  if (v >= 1 && v <= 31) setPayday(v);
                }}
                data-testid="onboarding-payday-input"
                className="mono h-11 w-20 rounded-[6px] border border-white/[0.12] bg-[#0D0D14] px-3 text-center text-sm font-light tabular text-white focus:border-[#00FF87]/50 focus:outline-none"
              />
            </div>
            <div className="flex-1" />
            <button
              onClick={() => setStep(3)}
              data-testid="onboarding-payday-next-button"
              className="h-12 w-full rounded-[6px] border border-white/[0.15] bg-transparent text-[11px] font-medium uppercase tracking-[0.25em] text-white/85 hover:bg-white/[0.05] active:scale-[0.98]"
            >
              Avanti
            </button>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div
            key="s3"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={fade}
            className="flex flex-1 flex-col pt-12"
          >
            <p className="caps-tag text-[#00FF87]/70">03 / 03</p>
            <h2 className="mt-3 text-2xl font-light tracking-[-0.02em] text-white/95">Budget mensile stimato?</h2>
            <p className="mt-1.5 text-sm font-light text-white/45">Quanto pensi di spendere in un mese tipico.</p>
            <div className="mt-8 flex items-center gap-3 rounded-[12px] border border-white/[0.1] bg-[#0D0D14] px-4 focus-within:border-[#00FF87]/40">
              <span className="mono text-xl font-light text-[#00FF87]">€</span>
              <input
                type="number"
                inputMode="decimal"
                placeholder="2500"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                data-testid="onboarding-monthly-budget-input"
                className="mono h-14 w-full bg-transparent text-2xl font-light tabular text-white placeholder:text-white/20 focus:outline-none"
              />
            </div>
            <p className="mt-3 text-xs font-light text-white/35">Serve solo come riferimento: l'agente impara dai dati reali.</p>
            <div className="flex-1" />
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="onboarding-submit-button"
              className="h-12 w-full rounded-[6px] border border-[#00FF87]/40 bg-transparent text-[11px] font-medium uppercase tracking-[0.25em] text-[#00FF87] shadow-[0_0_24px_rgba(0,255,135,0.1)] hover:bg-[#00FF87]/[0.06] active:scale-[0.98] disabled:opacity-50"
            >
              Inizia
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
